import { Component, ViewChildren, QueryList, ElementRef, AfterViewInit, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ConfirmationService } from '../../shared/services/confirmation.service';

@Component({
  selector: 'app-reset-password-code',
  templateUrl: './reset-password-code.component.html',
  styleUrls: ['./reset-password-code.component.css']
})
export class ResetPasswordCodeComponent implements OnInit, AfterViewInit {
  @ViewChildren('codeInput0,codeInput1,codeInput2,codeInput3,codeInput4,codeInput5') 
  codeInputs!: QueryList<ElementRef>;

  code: string[] = ['', '', '', '', '', ''];
  resetPasswordForm!: FormGroup;
  isLoading = false;
  submitted = false;
  userEmail: string | null = null;
  showPassword = false;
  showConfirmPassword = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    // Inicializar o formulário
    this.resetPasswordForm = this.formBuilder.group({
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/)
      ]],
      confirmPassword: ['', Validators.required]
    });

    // Verificar se existe o parâmetro email na URL
    this.route.queryParams.subscribe(params => {
      this.userEmail = params['email'];
      
      if (!this.userEmail) {
        this.confirmationService.confirm({
          title: 'Erro',
          message: 'Email não informado. Por favor, acesse o link enviado por email novamente.',
          confirmText: 'Ir para login',
          cancelText: 'Fechar',
          confirmButtonClass: 'btn-primary',
          cancelButtonClass: 'btn-secondary',
          type: 'danger'
        }).subscribe(confirmed => {
          if (confirmed) {
            this.router.navigate(['/login']);
          }
        });
        return;
      }
      
    });
  }
  ngAfterViewInit() {
    // Focar no primeiro campo quando a página carregar
    setTimeout(() => {
      const inputs = this.codeInputs.toArray();
      if (inputs[0]) {
        inputs[0].nativeElement.focus();
      }
    }, 100);
  }

  onCodeInput(event: any, index: number) {
    const input = event.target;
    const value = input.value;
    
    // Permitir apenas números
    if (!/^\d*$/.test(value)) {
      this.code[index] = '';
      return;
    }
    
    this.code[index] = value;
    
    // Mover para o próximo campo se um dígito foi digitado
    if (value && index < 5) {
      this.focusNextInput(index + 1);
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    // Permitir navegação com setas
    if (event.key === 'ArrowLeft' && index > 0) {
      this.focusNextInput(index - 1);
    } else if (event.key === 'ArrowRight' && index < 5) {
      this.focusNextInput(index + 1);
    }
    
    // Permitir backspace para voltar ao campo anterior
    if (event.key === 'Backspace' && !this.code[index] && index > 0) {
      this.focusNextInput(index - 1);
    }
  }

  focusNextInput(index: number) {
    const inputs = this.codeInputs.toArray();
    if (inputs[index]) {
      inputs[index].nativeElement.focus();
    }
  }

  isCodeComplete(): boolean {
    return this.code.every(digit => digit !== '');
  }

  getCodeString(): string {
    return this.code.join('');
  }

  verifyCode() {
    this.submitted = true;

    // Validar código completo
    if (!this.isCodeComplete()) {
      this.confirmationService.confirm({
        title: 'Erro',
        message: 'Por favor, digite o código completo.',
        confirmText: 'OK',
        cancelText: 'Fechar',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        type: 'danger'
      }).subscribe();
      return;
    }

    // Validar formulário
    if (this.resetPasswordForm.invalid) {
      this.confirmationService.confirm({
        title: 'Erro',
        message: 'Preencha os campos de senha corretamente.',
        confirmText: 'OK',
        cancelText: 'Fechar',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        type: 'danger'
      }).subscribe();
      return;
    }

    // Validar se as senhas coincidem
    const password = this.resetPasswordForm.get('password')?.value;
    const confirmPassword = this.resetPasswordForm.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      this.confirmationService.confirm({
        title: 'Erro',
        message: 'As senhas não coincidem.',
        confirmText: 'OK',
        cancelText: 'Fechar',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        type: 'danger'
      }).subscribe();
      return;
    }

    if (!this.userEmail) {
      this.confirmationService.confirm({
        title: 'Erro',
        message: 'Email do usuário não encontrado.',
        confirmText: 'Ir para login',
        cancelText: 'Fechar',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'btn-secondary',
        type: 'danger'
      }).subscribe(confirmed => {
        if (confirmed) {
          this.router.navigate(['/login']);
        }
      });
      return;
    }

    const code = this.getCodeString();
    this.isLoading = true;
    
    // Chamar o serviço de autenticação para validar código e resetar senha
    this.authService.passwordCodeValidate(this.userEmail, code, password).subscribe({
      next: (resp) => {
        this.isLoading = false;
        if (resp && resp.success) {
          this.confirmationService.confirm({
            title: 'Sucesso',
            message: 'Senha alterada com sucesso!',
            confirmText: 'Ir para login',
            cancelText: 'Fechar',
            confirmButtonClass: 'btn-primary',
            cancelButtonClass: 'btn-secondary',
            type: 'info'
          }).subscribe(confirmed => {
            if (confirmed) {
              this.router.navigate(['/login']);
            }
          });
        } else {
          this.confirmationService.confirm({
            title: 'Erro',
            message: resp?.message || 'Código inválido ou expirado. Tente novamente.',
            confirmText: 'OK',
            cancelText: 'Fechar',
            confirmButtonClass: 'btn-primary',
            cancelButtonClass: 'btn-secondary',
            type: 'danger'
          }).subscribe();
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Erro ao resetar senha:', error);
        this.confirmationService.confirm({
          title: 'Erro',
          message: 'Erro ao resetar senha. Tente novamente.',
          confirmText: 'OK',
          cancelText: 'Fechar',
          confirmButtonClass: 'btn-primary',
          cancelButtonClass: 'btn-secondary',
          type: 'danger'
        }).subscribe();
      }
    });
  }



  goBack() {
    this.router.navigate(['/login']);
  }

  clearCode() {
    this.code = ['', '', '', '', '', ''];
    // Focar no primeiro campo
    setTimeout(() => {
      const inputs = this.codeInputs.toArray();
      if (inputs[0]) {
        inputs[0].nativeElement.focus();
      }
    }, 100);
  }

  // Getters para facilitar acesso aos campos do formulário
  get f() { return this.resetPasswordForm.controls; }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
} 